import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Button, Popconfirm, Spin, Tabs, Tag, Tooltip, message } from 'antd';
import { debounce } from 'lodash';

import EligibilityLookup from './EligibilityLookup';
import {
  CorsiaCatcherData,
  getCorsiaCatcher,
  updateCorsiaCatcher,
  updateCorsiaWorkflow,
} from '../../api/corsia';

const QUERY_KEY = 'corsia-catcher';

export const defaultData: CorsiaCatcherData = {
  eligibility_lookup: { data: [] },
  participation: { data: [] },
};

const CorsiaCatcher: React.FC = () => {
  const queryClient = useQueryClient();
  const [data, setData] = useState<CorsiaCatcherData>(defaultData);
  const [isDataInitialized, setIsDataInitialized] = useState(false);
  const [workflowState, setWorkflowState] = useState<'staging' | 'production'>('staging');
  const debounceSaveReq = useRef<any>(null);

  const { isLoading } = useQuery(QUERY_KEY, getCorsiaCatcher, {
    onSuccess: (fetched) => {
      setData({ ...defaultData, ...(fetched?.json_data ?? {}) });
      setWorkflowState(fetched?.workflow_state || 'staging');
      setIsDataInitialized(true);
    },
    onError: () => {
      message.error('Failed to load the CORSIA catcher — starting from defaults');
      setData(defaultData);
      setIsDataInitialized(true);
    },
  });

  const { mutateAsync: saveAsync, isLoading: isSaving } = useMutation(
    (updated: CorsiaCatcherData) => updateCorsiaCatcher({ json_data: updated }),
    {
      onMutate: async (updated) => {
        await queryClient.cancelQueries(QUERY_KEY);
        const previous = queryClient.getQueryData(QUERY_KEY);
        queryClient.setQueryData(QUERY_KEY, (old: any) => ({ ...old, json_data: updated }));
        return { previous };
      },
      onError: (_err, _updated, context: any) => {
        queryClient.setQueryData(QUERY_KEY, context?.previous);
        message.error('Failed to save changes');
      },
      onSuccess: () => {
        message.success('Changes saved successfully');
      },
      onSettled: () => {
        queryClient.invalidateQueries(QUERY_KEY);
      },
    }
  );

  const { mutate: updateWorkflow, isLoading: isUpdatingWorkflow } = useMutation(
    (payload: { state: 'staging' | 'production' }) => updateCorsiaWorkflow(payload),
    {
      onSuccess: (_res, payload) => {
        setWorkflowState(payload.state);
        message.success(`Moved to ${payload.state} successfully`);
        queryClient.invalidateQueries(QUERY_KEY);
      },
      onError: () => {
        message.error('Failed to update workflow state');
      },
    }
  );

  // Auto-save only while staging, matching the compass module.
  const debouncedSave = useMemo(() => {
    debounceSaveReq.current = debounce((updated: CorsiaCatcherData) => {
      if (isDataInitialized && workflowState === 'staging') {
        // onError already surfaces the failure; swallow the rejection so the
        // debounce timer doesn't raise an unhandled promise rejection, which
        // CRA renders as a fatal error overlay.
        return saveAsync(updated).catch(() => undefined);
      }
    }, 5000);
    return debounceSaveReq.current as any;
  }, [saveAsync, isDataInitialized, workflowState]);

  const readonly = workflowState === 'production';

  const onSectionChange = useCallback(
    (section: keyof CorsiaCatcherData, rows: any[], patch?: Record<string, unknown>) => {
      if (readonly) return;
      setData((prev) => {
        const next = { ...prev, [section]: { ...prev[section], ...patch, data: rows } };
        debouncedSave(next);
        return next;
      });
    },
    [debouncedSave, readonly]
  );

  const handleWorkflowToggle = async () => {
    const flushed = debounceSaveReq.current?.flush?.();
    if (flushed && typeof (flushed as any).then === 'function') {
      try {
        await flushed;
      } catch {
        // the workflow mutation surfaces its own errors
      }
    }
    updateWorkflow({ state: workflowState === 'staging' ? 'production' : 'staging' });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spin />
      </div>
    );
  }

  const movingTo = workflowState === 'staging' ? 'production' : 'staging';

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold m-0">CORSIA catcher</h1>
          <Tag color={readonly ? 'green' : 'blue'}>{workflowState}</Tag>
          {isSaving && <Tag>saving…</Tag>}
        </div>
        {movingTo === 'production' ? (
          <Popconfirm
            title="Move to production?"
            description="This publishes the current CORSIA content and locks editing."
            onConfirm={handleWorkflowToggle}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" loading={isUpdatingWorkflow}>
              Move to production
            </Button>
          </Popconfirm>
        ) : (
          <Button type="primary" loading={isUpdatingWorkflow} onClick={handleWorkflowToggle}>
            Move to staging
          </Button>
        )}
      </div>

      {readonly && (
        <Tooltip title="Move back to staging to edit">
          <p className="text-sm text-gray-500">
            This catcher is in production and is read-only.
          </p>
        </Tooltip>
      )}

      <Tabs
        defaultActiveKey="eligibility-lookup"
        items={[
          {
            key: 'eligibility-lookup',
            label: 'Eligibility lookup',
            children: (
              <EligibilityLookup
                rows={data.eligibility_lookup?.data ?? []}
                seededAt={data.eligibility_lookup?.seeded_at}
                onChange={(rows, patch) => onSectionChange('eligibility_lookup', rows, patch)}
                readonly={readonly}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

export default CorsiaCatcher;
